import { Info, ShieldAlert, TriangleAlert } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

import type { Finding } from "../data/review-data"

/*
   The navigator dock's presentational parts.

   Split out of `pdf-controls` to keep that file under the 300-line limit: these
   render and do not decide anything, so they are the half that can move without
   taking any behaviour with it.
*/

export function PillButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "grid size-8 shrink-0 place-items-center rounded-full text-text-2 transition-colors duration-120 ease-out",
        "hover:bg-surface-sunken hover:text-text",
        "disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      {children}
    </button>
  )
}

/** The finding's severity as a single mark, for a control with no room for a word. */
export function SeverityGlyph({ severity }: { severity: Finding["severity"] }): JSX.Element {
  const Icon = severity === "error" ? ShieldAlert : severity === "warning" ? TriangleAlert : Info
  return (
    <Icon
      aria-hidden="true"
      className={cn(
        "size-3.5 shrink-0",
        severity === "error"
          ? "text-[color:var(--err)]"
          : severity === "warning"
            ? "text-[color:var(--print)]"
            : "text-[color:var(--info)]",
      )}
    />
  )
}

export function PillDivider(): JSX.Element {
  return <span aria-hidden="true" className="h-4 w-px bg-border" />
}

/**
 * Pure-CSS hover chip for the pill's active finding. Renders a summary card
 * on hover/focus without any state, event handlers, or portalling — so it
 * cannot capture clicks on sibling controls (the zoom button) or linger
 * after the reviewer has moved on.
 *
 * A previous version used Radix Popover with mouse enter/leave timers; on
 * hover it stayed open, covering the finding once the zoom animation
 * scrolled it into place. Reviewers had to hunt for their mouse to dismiss
 * it. This version disappears the moment the pointer leaves the chip.
 */
export function FindingHoverChip({ finding }: { finding: Finding }): JSX.Element {
  const pct = finding.confidence == null ? null : Math.round(finding.confidence * 100)

  return (
    <span className="group/chip relative flex min-w-0 flex-1 items-center gap-1.5">
      {/* The same badge the page draws on the finding's box, so the control and
          the mark read as one thing, followed by the claim rather than the rule
          id: a reviewer stepping through findings is reading what is wrong, not
          which rule caught it. */}
      <span className="grid size-5 shrink-0 place-items-center rounded-full bg-text font-id text-[10px] font-semibold tabular-nums text-surface">
        {finding.number}
      </span>
      <SeverityGlyph severity={finding.severity} />
      <span className="min-w-0 flex-1 truncate font-medium text-text">{finding.claim}</span>
      <span
        role="tooltip"
        className={cn(
          // Opens downward. The dock sits at the top of the pane, so a panel
          // anchored to `bottom-full` would open past the top edge and be
          // clipped — it has the whole page beneath it instead.
          "pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-[22rem] -translate-x-1/2",
          "rounded-xl border border-border bg-surface p-4 text-left shadow-e3",
          // `hidden`, not `opacity-0` or `invisible`: both of those keep the layout box, and
          // an absolutely positioned panel still counts toward an ancestor's scrollHeight.
          // A long finding's master/printed text makes this one ~900px, which pushed the
          // workspace past the viewport and scrolled the review header off the top.
          "hidden opacity-0 transition-opacity duration-100 ease-out",
          "group-hover/chip:block group-hover/chip:opacity-100",
          "group-focus-within/chip:block group-focus-within/chip:opacity-100",
        )}
      >
        {/* The finding's number in the badge the page draws on its box, so the
            panel and the mark it describes are recognisably the same thing. */}
        <span className="mb-2 flex items-start gap-2">
          <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-text font-id text-[10.5px] font-semibold tabular-nums text-surface">
            {finding.number}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-body-sm font-semibold leading-snug text-text">
              {finding.claim}
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 font-id text-[10.5px] text-text-3">
              <span className="uppercase tracking-[0.06em]">{finding.ruleId}</span>
              {pct != null && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="tabular-nums">Conf: {pct}%</span>
                </>
              )}
              {finding.occurrences > 1 && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="tabular-nums">{finding.occurrences}x on label</span>
                </>
              )}
            </span>
          </span>
        </span>

        {finding.reasoning && (
          <span className="mb-3 block text-body-sm leading-[1.5] text-text-2">
            {finding.reasoning}
          </span>
        )}

        {finding.divergence && (finding.divergence.master || finding.divergence.printed) && (
          /* The evidence as a framed block, not two sentences: the reviewer is
             comparing two strings word by word, and a shared frame with labelled
             rows is what makes the difference between them visible. */
          <span className="mb-3 block space-y-2 rounded-lg bg-surface-sunken p-2.5">
            {finding.divergence.master != null && (
              <span className="block">
                <span className="block font-id text-[10px] uppercase tracking-[0.08em] text-master">
                  Master
                </span>
                <span className="mt-0.5 block font-id text-[11.5px] leading-[1.45] text-text">
                  {finding.divergence.master || "—"}
                </span>
              </span>
            )}
            {finding.divergence.printed != null && (
              <span className="block border-t border-border-soft pt-2">
                <span className="block font-id text-[10px] uppercase tracking-[0.08em] text-print">
                  Printed label
                </span>
                <span className="mt-0.5 block font-id text-[11.5px] leading-[1.45] text-text">
                  {finding.divergence.printed || "—"}
                </span>
              </span>
            )}
          </span>
        )}

        {finding.authorities.length > 0 && (
          <span className="flex flex-wrap gap-1.5">
            {finding.authorities.map((a) => (
              <span
                key={a.label}
                className="rounded-md border border-border-soft px-1.5 py-0.5 font-id text-[10.5px] text-text-2"
              >
                {a.label}
              </span>
            ))}
          </span>
        )}
      </span>
    </span>
  )
}
